/**
 * 「浏览与编辑」字表 / 词表的**表头 ↔ 表尾**跳转按钮（2026-10-20）。
 *
 * ## 用户原话
 *
 * > "在**字表的上面加一个按钮，直接跳转到表尾**；在**表尾那里加一个按钮，跳转到表头**。"
 *
 * ## 为什么**始终显示**（不按"超过一屏"才出现）
 *
 * 用户给了两个选项，这里选**始终显示**：
 *   · 可预期：一屏能看完全部字时，两颗按钮点下去几乎不动 —— 无害，
 *     但界面不会因为"这次字少"而少一块、下次又冒出来；
 *   · 判"是否超过一屏"要量 DOM（`scrollHeight > clientHeight`），首帧与服务端渲染
 *     都拿不到，只能挂 effect 后补 —— 会出现"先没有、后冒出来"的跳动；
 *   · 判断逻辑一旦有偏差，用户会遇到"明明很长却没有跳转按钮"，比"短列表上多一颗按钮"糟。
 *
 * ## 滚动怎么实现
 *
 * 不用 `scrollIntoView`（会把列表顶塞到吸顶 header 下面），而是按列表矩形的
 * 顶 / 底 + 视口算 `window.scrollTo({ top })`，见 `lib/browseScrollJumps.ts`：
 *   · **平滑**：`behavior: 'smooth'`；
 *   · `prefers-reduced-motion: reduce` ⇒ `behavior: 'auto'`（瞬时），
 *     判据与"字卡跳转"共用 `hanziJump.ts` 的那一份。
 *
 * ⚠️ 只给「浏览与编辑」用（`DictationItemsPanel`）：⛔ 导入预览（`ImportListingPreview`）
 *    不要加这两颗按钮 —— 用户只说了字表/词表这边。
 */
import type { RefObject } from 'react';
import { Button } from '../ui/Button';
import {
  BROWSE_JUMP_TO_BOTTOM_LABEL,
  BROWSE_JUMP_TO_TOP_LABEL,
  browseScrollTarget,
  type BrowseScrollEdge,
} from '../../lib/browseScrollJumps';
import { hanziJumpScrollBehavior, prefersReducedMotion } from './hanziJump';

export interface BrowseScrollJumpsProps {
  /** 要滚动的那个列表容器（`<ul>`）。 */
  listRef: RefObject<HTMLElement | null>;
  /** 跳到哪一头：`bottom` = 表尾按钮（放在列表上方），`top` = 表头按钮（放在列表末尾）。 */
  edge: BrowseScrollEdge;
}

export function BrowseScrollJumps({ listRef, edge }: BrowseScrollJumpsProps) {
  const label = edge === 'bottom' ? BROWSE_JUMP_TO_BOTTOM_LABEL : BROWSE_JUMP_TO_TOP_LABEL;

  return (
    <Button
      size="md"
      variant="secondary"
      /** 手机可点性：`md` 已经是 h-11（44px），这里再显式兜一次底。 */
      className="min-h-[44px]"
      data-browse-scroll-jump={edge}
      aria-label={edge === 'bottom' ? '跳到字表最底部' : '跳到字表最顶部'}
      title={edge === 'bottom' ? '滚到列表最底部' : '滚到列表最顶部'}
      onClick={() => {
        const list = listRef.current;
        if (list === null) return;
        const rect = list.getBoundingClientRect();
        window.scrollTo({
          top: browseScrollTarget(
            rect,
            { scrollY: window.scrollY, innerHeight: window.innerHeight },
            edge,
          ),
          /** reduced-motion ⇒ `auto`（瞬时），否则平滑滚动。 */
          behavior: hanziJumpScrollBehavior(prefersReducedMotion()),
        });
      }}
    >
      {label}
    </Button>
  );
}
